import { Message } from '@twilio/conversations';
import { get } from 'lodash';
import smoothScrollIntoView from 'smooth-scroll-into-view-if-needed';

import { DataDogLogger } from 'utils/dataDog';
import { htmlId } from 'utils/html';

import { CHAT_UI_PREFIX, MESSAGES_LIST_BOTTOM_SPACER_ID } from 'constants/id';
import { PARTICIPANT_NAME_SYSTEM_ACTUAL, PARTICIPANT_NAME_SYSTEM_FRIENDLY } from 'constants/twilio';
import { containsUrl, convertLinksToHttps, getUrls, validateURL } from 'utils/url';
import { CHATUI_WINDOW_PATH } from 'constants/global';

import { TwilioAuthorType } from 'services/twilio/types';
import { FormattedMessagesType } from 'routes/Chat/Messages/types';
import { TEXT_FORMAT } from 'routes/Chat/Messages/constants';
import { AVATAR_ACTIVITY_STATE } from 'components/MessageExtensions/AvatarUpdate/AvatarUpdate.types';

/**
 * Aggregator. Gather an array of formatted conversation messages suited for Carvana's context.
 *
 * @param rawMessages raw JSON messages from Twilio API.
 * @param startIndex current index of message that needs parsing. Increment from end when new message added.
 */
export function formatMessages(
  rawMessages: Array<Message>,
  startIndex = 0,
): Array<FormattedMessagesType> {
  const messagesArr: Array<FormattedMessagesType> = [];
  rawMessages.forEach((rawMessage, messageIndex) => {
    const formattedMessage = formatMessage(rawMessage, messageIndex);

    if (!formattedMessage) return;

    messagesArr.push({
      ...formattedMessage,
      index: startIndex + messageIndex,
    });
  });

  return messagesArr;
}

/**
 * Formatter for a singular Twilio message. Read and convert raw JSON data
 * from Twilio into a format that is suited for Carvana's context.
 *
 * @param rawMessage JSON message from Twilio that needs to be formatted and parsed.
 * @param messageIndex index of parent Twilio message in conversation.
 */
export function formatMessage(
  rawMessage: Message,
  messageIndex?: number,
): FormattedMessagesType | null {
  if (!rawMessage || !rawMessage.body) return null;

  const jsonBody = JSON.parse(rawMessage.body);

  return {
    author: get(rawMessage, 'state.author', ''),
    text: get(jsonBody, 'text', null),
    textFormat: get(jsonBody, 'textFormat', TEXT_FORMAT.LEGACY),
    sid: rawMessage.sid,
    srcPayload: get(jsonBody, 'payload', null),
    extension: get(jsonBody, 'extension') ?? null,
    index: messageIndex || 0,
    htmlId: htmlId(`message-container-${messageIndex}`),
    timestamp: rawMessage.dateCreated ?? rawMessage.dateUpdated ?? new Date(),
  };
}

/**
 * Scroll to explicitly defined spacer DOM element at bottom of chat messages container.
 * Without this, container will ignore padding/margin after typing animations.
 *
 * The setTimeout prevents:
 * 1) "partial" scroll which doesn't go to bottom. 2) jumping scroll for IntersectionObservers
 */
export const scrollToMessagesListBottom = (duration?: number): void => {
  const spacerElem = document.getElementById(`${CHAT_UI_PREFIX}-${MESSAGES_LIST_BOTTOM_SPACER_ID}`);

  const ua = navigator.userAgent;
  const isSafari = /safari/i.test(ua) && !/chrome/i.test(ua); // ref: https://eightify.app/dev/how-to-detect-safari-chrome-ie-firefox-and-opera-browsers

  setTimeout(() => {
    if (isSafari) {
      // apply polyfill. Safari doesn't normally support smooth scrolling.
      spacerElem &&
        smoothScrollIntoView(spacerElem, {
          block: 'end',
          behavior: 'smooth',
          ...(duration && { duration }),
        });
    } else {
      spacerElem && spacerElem.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, 250);
};

/**
 * Find latest message in conversation by the specified author
 * @param author Twilio prefix/convention for author.
 * @return formatted latest message from specified author. Or null if not found.
 */
export const getLastMessageFromAuthor = async (
  author: TwilioAuthorType,
): Promise<FormattedMessagesType | null> => {
  const messages = await window[CHATUI_WINDOW_PATH].getMessages();
  let lastMsgFromAuthor;
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages?.[i];
    if (msg && msg.author && msg.author.includes(author)) {
      lastMsgFromAuthor = msg;
      break;
    }
  }
  return lastMsgFromAuthor || null;
};

/**
 * Get the HTML element from conversation's message list
 *
 * @param {number} index to grab message out of conversation from
 * @return {HTMLElement | null}
 */
export const getMessageHtmlElem = {
  byIndex: (index: number) => {
    const idMessageContainer = htmlId(`message-container-${index}`);
    return document.getElementById(idMessageContainer) || null;
  },
};

/**
 * Used to convert all links in a user message to be HTTPS before they are sent to Twilio.
 * @param message
 * @returns
 */
export const secureUserMessage = (message: string) => {
  const convertResult = convertLinksToHttps(message);
  if (convertResult.message !== message) {
    DataDogLogger.error(new Error('Invalid URL(s) found'), {
      from: 'user',
      urls: convertResult.invalidUrls,
    });
  }
  return convertResult.message;
};

/**
 * Used to check a message to see if it has invalid URLs.
 * @param message
 */
export const checkMessageForInsecureUrls = (message: Message) => {
  const msg = formatMessage(message);

  const errorContext = {
    from:
      msg?.author === PARTICIPANT_NAME_SYSTEM_ACTUAL
        ? PARTICIPANT_NAME_SYSTEM_FRIENDLY
        : 'advocate',
  };

  if (msg?.text && containsUrl(msg.text)) {
    const urls = getUrls(msg.text) ?? [];

    for (const url of urls)
      if (!validateURL(url))
        DataDogLogger.error(new Error('Invalid URL(s) found'), { urls: [url], ...errorContext });
  }

  const msgExtension = JSON.stringify(msg?.extension);

  if (msg?.extension && containsUrl(msgExtension)) {
    const urls = getUrls(msgExtension) ?? [];
    for (const url of urls)
      if (!validateURL(url))
        DataDogLogger.error(new Error('Invalid URL found'), { url, ...errorContext });
  }
};

export const getLastAvatarUpdate = (messages: FormattedMessagesType[]) => {
  if (!messages || !messages.length) return false;
  // Avatar update state could be in messageBodies or just the extension.
  const lastAvatarUpdateMsg = messages
    .slice()
    .reverse()
    .find(
      (message: FormattedMessagesType) =>
        message.extension && message.extension.hasOwnProperty('avatarUpdate'),
    );
  const lastAvatarUpdateMsgBodies = messages
    .slice()
    .reverse()
    .find(
      (message: FormattedMessagesType) =>
        message.extension?.messageBodies &&
        message.extension?.messageBodies?.list[0]?.extension?.hasOwnProperty('avatarUpdate'),
    );
  // No found avatar update messages, not in a queue.
  if (!lastAvatarUpdateMsg && !lastAvatarUpdateMsgBodies) {
    return false;
  }
  // Find the most recent message with an avatar update state.
  const lastMessage =
    lastAvatarUpdateMsg && lastAvatarUpdateMsgBodies
      ? lastAvatarUpdateMsg?.timestamp?.getTime() > lastAvatarUpdateMsgBodies?.timestamp?.getTime()
        ? lastAvatarUpdateMsg
        : lastAvatarUpdateMsgBodies
      : lastAvatarUpdateMsg ?? lastAvatarUpdateMsgBodies;
  // Get the state from the message.
  const avatarUpdateState =
    lastMessage?.extension?.avatarUpdate?.state ||
    lastMessage?.extension?.messageBodies?.list[0]?.extension?.avatarUpdate?.state;

  return avatarUpdateState;
};

/**
 * Used to check messages to see if the user is currently in the Assignment Queue.
 * Expecting to see STATE_IN_QUEUE avatarUpdate status.
 * @return {boolean}
 */
export const getIsInAssignmentQueue = (messages: FormattedMessagesType[]): boolean => {
  const avatarUpdateState = getLastAvatarUpdate(messages);

  return avatarUpdateState === AVATAR_ACTIVITY_STATE.IN_QUEUE;
};

export const getIsAdvocateChatEndedByUser = (messages: FormattedMessagesType[]): boolean => {
  const avatarUpdateState = getLastAvatarUpdate(messages);

  return avatarUpdateState === AVATAR_ACTIVITY_STATE.DISMISSED;
};
